import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Anchor, Loader, Modal, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import {
  selectAutoSavePdfEnabled,
  selectAutoSavePdfFolderName,
} from "@store/userPreferences/userPreferencesSlice";
import {
  selectIsBackToBackEyeTest,
  selectTestConfiguration,
  selectTestId,
  selectTestStatus,
} from "@store/tests/testSelectors";
import { selectTestSuiteSessionId } from "@store/testSuite/testSuiteSelectors";
import { selectCompletedTestIds } from "@store/testsession/testSessionSelectors";
import { selectSelectedPatient } from "@store/patients/patientSelectors";
import { EyeType, TestStatus } from "types/tests/core";
import {
  buildAutoSaveFilename,
  isAutoSaveSupported,
  verifyPermission,
  writeBlobToHandle,
} from "@utils/autoSavePdf";
import { loadDirHandle } from "@utils/fileSystemHandleStore";

const IFRAME_TIMEOUT_MS = 60_000;
const MESSAGE_KIND = "vfa-autosave-blob";
const ERROR_KIND = "vfa-autosave-error";

interface Pending {
  testResultId: string;
  patientId: string;
  category: string;
  testType: string;
  eyePrefix: string | null;
  completedAt: number;
}

interface BlobMessage {
  kind: typeof MESSAGE_KIND;
  blob: Blob;
  testResultId: string;
}

interface ErrorMessage {
  kind: typeof ERROR_KIND;
  testResultId: string;
  reason?: string;
}

function isBlobMessage(data: unknown): data is BlobMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { kind?: unknown }).kind === MESSAGE_KIND &&
    (data as { blob?: unknown }).blob instanceof Blob &&
    typeof (data as { testResultId?: unknown }).testResultId === "string"
  );
}

function isErrorMessage(data: unknown): data is ErrorMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { kind?: unknown }).kind === ERROR_KIND &&
    typeof (data as { testResultId?: unknown }).testResultId === "string"
  );
}

export default function AutoSavePdfHost() {
  const navigate = useNavigate();
  const { t } = useTranslation(undefined, { useSuspense: false });
  const enabled = useSelector(selectAutoSavePdfEnabled);
  const folderName = useSelector(selectAutoSavePdfFolderName);
  const testStatus = useSelector(selectTestStatus);
  const testId = useSelector(selectTestId);
  const config = useSelector(selectTestConfiguration);
  const patient = useSelector(selectSelectedPatient);
  const suiteSessionId = useSelector(selectTestSuiteSessionId);
  const isBackToBack = useSelector(selectIsBackToBackEyeTest);
  const completedTestIds = useSelector(selectCompletedTestIds);
  const location = useLocation();
  const lastStatus = useRef<TestStatus | null>(null);
  const firstEyeIdRef = useRef<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  const isAutoSaveIframe =
    new URLSearchParams(location.search).get("autosave") === "1" && window.parent !== window;

  useEffect(() => {
    const prev = lastStatus.current;
    lastStatus.current = testStatus;
    if (prev === null) return;
    if (isAutoSaveIframe) return;

    if (testStatus === TestStatus.TestConfiguration && firstEyeIdRef.current) {
      firstEyeIdRef.current = null;
    }

    if (
      isBackToBack &&
      prev !== TestStatus.WaitingForNextEye &&
      testStatus === TestStatus.WaitingForNextEye
    ) {
      firstEyeIdRef.current = completedTestIds[0] ?? null;
    }

    if (!enabled) return;
    if (!isAutoSaveSupported()) return;
    if (suiteSessionId) return;
    if (prev === TestStatus.Completed || testStatus !== TestStatus.Completed) return;
    if (!testId || !patient?.uuid || !config?.name || !config.testType) return;

    let combinedId = testId;
    if (isBackToBack) {
      const partner =
        firstEyeIdRef.current && firstEyeIdRef.current !== testId
          ? firstEyeIdRef.current
          : completedTestIds.find((id) => id && id !== testId);
      if (partner) {
        combinedId = `${testId},${partner}`;
      }
    }
    firstEyeIdRef.current = null;

    let eyePrefix: string | null = null;
    if (!isBackToBack) {
      const eyeType = config.eyeConfiguration?.eyeType;
      if (eyeType === EyeType.RightEye) eyePrefix = "OD";
      else if (eyeType === EyeType.LeftEye) eyePrefix = "OS";
    }

    setPending({
      testResultId: combinedId,
      patientId: patient.uuid,
      category: config.name,
      testType: String(config.testType),
      eyePrefix,
      completedAt: Date.now(),
    });
  }, [
    testStatus,
    enabled,
    isAutoSaveIframe,
    suiteSessionId,
    isBackToBack,
    completedTestIds,
    testId,
    patient,
    config,
  ]);

  useEffect(() => {
    if (!pending) return;

    let iframe: HTMLIFrameElement | null = null;
    let timeoutId: number | null = null;
    let removed = false;

    const beforeUnload = (event: BeforeUnloadEvent): string => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    const cleanup = (): void => {
      if (removed) return;
      removed = true;
      window.removeEventListener("message", onMessage);
      window.removeEventListener("beforeunload", beforeUnload);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (iframe) {
        iframe.remove();
        iframe = null;
      }
      setPending(null);
    };

    const showSuccessToast = (folder: string): void => {
      notifications.show({
        color: "green",
        title: t("UserSettings.preferences.autoSavePdf.toast.successTitle"),
        message: t("UserSettings.preferences.autoSavePdf.toast.success", { folder }),
        autoClose: 4000,
      });
    };

    const showFailureToast = (kind: "generic" | "permission" = "generic"): void => {
      const messageKey =
        kind === "permission"
          ? "UserSettings.preferences.autoSavePdf.toast.permissionFailure"
          : "UserSettings.preferences.autoSavePdf.toast.failure";
      notifications.show({
        color: "red",
        title: t("UserSettings.preferences.autoSavePdf.toast.failureTitle"),
        message: (
          <Stack gap={4}>
            <Text size="sm">{t(messageKey)}</Text>
            <Anchor
              size="sm"
              component="button"
              type="button"
              onClick={() => navigate("/user-settings")}
            >
              {t("UserSettings.preferences.autoSavePdf.toast.openSettings")}
            </Anchor>
          </Stack>
        ),
        autoClose: 8000,
      });
    };

    const onMessage = async (event: MessageEvent): Promise<void> => {
      if (event.origin !== window.location.origin) return;
      if (!iframe || event.source !== iframe.contentWindow) return;

      if (isErrorMessage(event.data) && event.data.testResultId === pending.testResultId) {
        console.error("[autoSavePdf] iframe reported failure", event.data.reason);
        showFailureToast();
        cleanup();
        return;
      }

      if (!isBlobMessage(event.data)) return;
      if (event.data.testResultId !== pending.testResultId) return;

      try {
        const handle = await loadDirHandle();
        if (!handle) {
          showFailureToast("permission");
          return;
        }
        const granted = await verifyPermission(handle, "readwrite");
        if (!granted) {
          showFailureToast("permission");
          return;
        }
        const filename = buildAutoSaveFilename(
          pending.patientId,
          pending.category,
          pending.testType,
          pending.completedAt,
          pending.eyePrefix,
        );
        await writeBlobToHandle(handle, filename, event.data.blob);
        showSuccessToast(folderName ?? handle.name);
      } catch (err) {
        console.error("[autoSavePdf] write failed", err);
        showFailureToast();
      } finally {
        cleanup();
      }
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("beforeunload", beforeUnload);

    iframe = document.createElement("iframe");
    iframe.src = `/test-result?uuid=${encodeURIComponent(pending.testResultId)}&autosave=1`;
    iframe.title = "vfa-autosave";
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText =
      "position:fixed;left:-99999px;top:-99999px;width:1500px;height:2100px;opacity:0;pointer-events:none;border:0;";
    document.body.appendChild(iframe);

    timeoutId = window.setTimeout(() => {
      console.warn("[autoSavePdf] iframe timed out");
      showFailureToast();
      cleanup();
    }, IFRAME_TIMEOUT_MS);

    return cleanup;
  }, [pending, folderName, navigate, t]);

  if (!pending) return null;
  return (
    <Modal
      opened
      onClose={() => undefined}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      size="sm"
      overlayProps={{ backgroundOpacity: 0.6, blur: 2 }}
      zIndex={10000}
    >
      <Stack align="center" gap="md" py="md">
        <Loader />
        <Text fw={600}>{t("UserSettings.preferences.autoSavePdf.overlay.title")}</Text>
        <Text size="sm" c="dimmed" ta="center">
          {t("UserSettings.preferences.autoSavePdf.overlay.message")}
        </Text>
      </Stack>
    </Modal>
  );
}
